import ApplicationConfig from '@/screens/ApplicationConfig';
import { BatchActionDetail } from '@/screens/BatchActionDetail';
import { BatchActionList } from '@/screens/BatchActionList';
import { BrandCreate } from '@/screens/BrandCreate';
import { BrandEdit } from '@/screens/BrandEdit';
import { BrandList } from '@/screens/BrandList';
import { CampaignCreate } from '@/screens/CampaignCreate';
import { CampaignDetails } from '@/screens/CampaignDetails';
import { CampaignEdit } from '@/screens/CampaignEdit';
import { CampaignList } from '@/screens/CampaignList';
import { Dashboard } from '@/screens/Dashboard';
import LandingPage from '@/screens/LandingPage';
import { FacilityCreate } from '@/screens/FacilityCreate';
import { FacilityDetails } from '@/screens/FacilityDetails';
import { FacilityEdit } from '@/screens/FacilityEdit';
import { FacilityList } from '@/screens/FacilityList';
import Login from '@/screens/Login';
import LoginDefinePassword from '@/screens/LoginDefinePassword';
import LoginUpdateContactInfo from '@/screens/LoginUpdateContactInfo';
import LoginVerificationCodeReceive from '@/screens/LoginVerificationCodeReceive';
import LoginVerificationCodeValidate from '@/screens/LoginVerificationCodeValidate';
import { MaterialCreate } from '@/screens/MaterialCreate';
import { MaterialEdit } from '@/screens/MaterialEdit';
import { MaterialList } from '@/screens/MaterialList';
import { MaterialTypeCreate } from '@/screens/MaterialTypeCreate';
import { MaterialTypeEdit } from '@/screens/MaterialTypeEdit';
import { MaterialTypeList } from '@/screens/MaterialTypeList';
import { MediaBrandList } from '@/screens/MediaBrandList';
import { MediaCreate } from '@/screens/MediaCreate';
import { MediaEdit } from '@/screens/MediaEdit';
import { MediaList } from '@/screens/MediaList';
import { RoleCreate } from '@/screens/RoleCreate';
import { RoleEdit } from '@/screens/RoleEdit';
import { UserCreate } from '@/screens/UserCreate';
import { UserEdit } from '@/screens/UserEdit';
import { UserList } from '@/screens/UserList';
import { WhatsAppNotifications } from '@/screens/WhatsAppNotifications';
import { User } from '@/store/userStore';
import type { JSX, ReactNode } from 'react';
import type { Location, NavigateFunction, Params } from 'react-router-dom';
import { BatchActionModal } from '@/components/BatchActionModal';
import { UserHierarchy } from '@/screens/UserHierarchy';

type BreadcrumbItem = {
  label: string;
  href?: string;
};
type BreadcrumbGenerator = (params: Params<string>, state?: Location['state']) => BreadcrumbItem[];

type getButton = (navigate: NavigateFunction) => {
  label: string;
  iconName?: string;
  iconFill?: 1 | 0;
  requiredScopes?: string[];
  style?: 'default' | 'outline';
  onClick: () => void;
  modal?: (args: { open: boolean; setOpen: (open: boolean) => void }) => ReactNode;
};

type RouteMeta = {
  title: string | ((user: User | null) => string);
  breadcrumb: BreadcrumbItem[] | BreadcrumbGenerator;
  backButton?: boolean;
  getButton?: getButton[];
};

export type RouteWithMeta = {
  path: string;
  element: JSX.Element;
  requiresAuth?: boolean;
  requiresScopes?: string[];
  requiresMaster?: boolean;
  redirectToLogin?: boolean;
  meta: RouteMeta;
};

export const routesWithMeta: RouteWithMeta[] = [
  {
    path: '/',
    element: <LandingPage />,
    requiresAuth: false,
    meta: {
      title: 'Shelflix - Gestão de Campanhas Digitais',
      breadcrumb: [],
    },
  },
  {
    path: '/dashboard',
    element: <Dashboard />,
    requiresAuth: true,
    requiresScopes: ['dashboard:view'],
    meta: {
      title: (user) => `Olá, ${user?.name || 'usuário'}`,
      breadcrumb: [],
      getButton: [
        (navigate) => ({
          label: 'Suporte',
          iconName: 'contact_phone',
          onClick: () => navigate('/dashboard'),
        }),
      ],
    },
  },
  {
    path: '/application-config',
    element: <ApplicationConfig />,
    requiresAuth: true,
    requiresScopes: ['admin:theme:manage'],
    meta: {
      title: 'Aparência',
      breadcrumb: [
        {
          label: 'Configuração de aparência',
          href: '/theme',
        },
      ],
    },
  },
  {
    path: '/user',
    element: <UserList />,
    requiresAuth: true,
    requiresScopes: ['admin:users:view'],
    meta: {
      title: 'Equipe e acessos',
      breadcrumb: [
        {
          label: 'Resumo',
          href: '/user',
        },
      ],
      getButton: [
        (navigate) => ({
          label: 'Cadastrar novo usuário',
          iconName: 'person',
          iconFill: 1,
          requiredScopes: ['admin:users:create'],
          onClick: () => navigate('/user/create'),
        }),
      ],
    },
  },
  {
    path: '/user/create',
    element: <UserCreate />,
    requiresAuth: true,
    requiresScopes: ['admin:users:create'],
    meta: {
      title: 'Equipe e acessos',
      breadcrumb: [
        { label: 'Resumo', href: '/user' },
        { label: 'Novo Usuário', href: '/user/create' },
      ],
      backButton: true,

      // getButton: (navigate) => ({
      //   label: 'Voltar',
      //   iconName: 'arrow_back_ios',
      //   onClick: () => navigate(-1),
      // }),
    },
  },
  {
    path: '/user/:uuid',
    element: <UserEdit />,
    requiresAuth: true,
    requiresScopes: ['admin:users:edit'],
    meta: {
      title: 'Equipe e acessos',
      breadcrumb: [
        { label: 'Resumo', href: '/user' },
        { label: 'Editar Usuário', href: '/user/:uuid' },
      ],
      backButton: true,

      // getButton: (navigate) => ({
      //   label: 'Voltar',
      //   iconName: 'arrow_back_ios',
      //   onClick: () => navigate(-1),
      // }),
    },
  },
  {
    path: '/hierarchy',
    element: <UserHierarchy />,
    requiresAuth: true,
    requiresScopes: ['admin:hierarchy:view'],
    meta: {
      title: 'Hierarquia de usuários',
      breadcrumb: [
        {
          label: 'Resumo',
          href: '/hierarchy',
        },
      ],
    },
  },
  {
    path: '/brand',
    element: <BrandList />,
    requiresAuth: true,
    requiresScopes: ['admin:brands:view'],
    meta: {
      title: 'Marcas',
      breadcrumb: [
        {
          label: 'Resumo',
          href: '/brand',
        },
      ],
      getButton: [
        (navigate) => ({
          label: 'Cadastrar nova marca',
          iconName: 'label',
          iconFill: 1,
          requiredScopes: ['admin:brands:create'],
          onClick: () => navigate('/brand/create'),
        }),
      ],
    },
  },
  {
    path: '/brand/create',
    element: <BrandCreate />,
    requiresAuth: true,
    requiresScopes: ['admin:brands:create'],
    meta: {
      title: 'Marcas',
      breadcrumb: [
        {
          label: 'Resumo',
          href: '/brand',
        },
        {
          label: 'Cadastrar nova marca',
          href: '/brand/create',
        },
      ],
      backButton: true,

      // getButton: (navigate) => ({
      //   label: 'Voltar',
      //   iconName: 'arrow_back_ios',
      //   onClick: () => navigate(-1),
      // }),
    },
  },
  {
    path: '/brand/:uuid',
    element: <BrandEdit />,
    requiresAuth: true,
    requiresScopes: ['admin:brands:view'],
    meta: {
      title: 'Marcas',
      breadcrumb: [
        { label: 'Resumo', href: '/brand' },
        { label: 'Editar marca', href: '/brand/:uuid' },
      ],
      backButton: true,

      // getButton: (navigate) => ({
      //   label: 'Voltar',
      //   iconName: 'arrow_back_ios',
      //   onClick: () => navigate(-1),
      // }),
    },
  },
  {
    path: '/facility',
    element: <FacilityList />,
    requiresAuth: true,
    requiresScopes: ['admin:facilities:view'],
    meta: {
      title: 'Estabelecimentos',
      breadcrumb: [
        {
          label: 'Resumo',
          href: '/facility',
        },
      ],
      getButton: [
        (navigate) => ({
          label: 'Cadastrar novo estabelecimento',
          iconName: 'storefront',
          requiredScopes: ['admin:facilities:create'],
          onClick: () => navigate('/facility/create'),
        }),
      ],
    },
  },
  {
    path: '/facility/create',
    element: <FacilityCreate />,
    requiresAuth: true,
    requiresScopes: ['admin:facilities:create'],
    meta: {
      title: 'Estabelecimentos',
      breadcrumb: [
        {
          label: 'Cadastrar novo estabelecimento',
          href: '/facility',
        },
      ],
      backButton: true,

      // getButton: (navigate) => ({
      //   label: 'Voltar',
      //   iconName: 'arrow_back_ios',
      //   onClick: () => navigate(-1),
      // }),
    },
  },
  {
    path: '/facility/:uuid',
    element: <FacilityDetails />,
    requiresAuth: true,
    requiresScopes: ['admin:facilities:view'],
    meta: {
      title: 'Estabelecimentos',
      breadcrumb: [
        { label: 'Resumo', href: '/facility' },
        { label: 'Informações de Estabelecimento', href: '/facility/:uuid' },
      ],
      backButton: true,

      // getButton: (navigate) => ({
      //   label: 'Voltar',
      //   iconName: 'arrow_back_ios',
      //   onClick: () => navigate(-1),
      // }),
    },
  },
  {
    path: '/facility/edit/:uuid',
    element: <FacilityEdit />,
    requiresAuth: true,
    requiresScopes: ['admin:facilities:edit'],
    meta: {
      title: 'Estabelecimentos',
      breadcrumb: [
        { label: 'Resumo', href: '/facility' },
        { label: 'Editar estabelecimento', href: '/facility/edit/:uuid' },
      ],
      backButton: true,

      // getButton: (navigate) => ({
      //   label: 'Voltar',
      //   iconName: 'arrow_back_ios',
      //   onClick: () => navigate(-1),
      // }),
    },
  },
  {
    path: '/material',
    element: <MaterialTypeList />,
    requiresAuth: true,
    requiresScopes: ['admin:material_type:manage'],
    meta: {
      title: 'Catálogo de materiais',
      breadcrumb: [
        {
          label: 'Resumo',
          href: '/material',
        },
      ],
      getButton: [
        (navigate) => ({
          label: 'Cadastrar novo material',
          iconName: 'assistant_on_hub',
          requiredScopes: ['admin:material_type:manage'],
          onClick: () => navigate('/material/create'),
        }),
      ],
    },
  },
  {
    path: '/pos/create',
    element: <MaterialCreate />,
    requiresAuth: true,
    requiresScopes: ['material:create'],
    meta: {
      title: 'Materiais de PDV',
      breadcrumb: [
        {
          label: 'Inicio',
          href: '/pos',
        },
        {
          label: 'Cadastrar novo material',
          href: '/pos/create',
        },
      ],
      backButton: true,

      // getButton: (navigate) => ({
      //   label: 'Voltar',
      //   iconName: 'arrow_back_ios',
      //   onClick: () => navigate(-1),
      // }),
    },
  },
  {
    path: '/material/create',
    element: <MaterialTypeCreate />,
    requiresAuth: true,
    requiresScopes: ['admin:material_type:manage'],
    meta: {
      title: 'Catálogo de materiais',
      breadcrumb: [
        {
          label: 'Resumo',
          href: '/material',
        },
        {
          label: 'Cadastrar novo material',
          href: '/material/create',
        },
      ],
      backButton: true,

      // getButton: (navigate) => ({
      //   label: 'Voltar',
      //   iconName: 'arrow_back_ios',
      //   onClick: () => navigate(-1),
      // }),
    },
  },
  {
    path: '/material/:uuid',
    element: <MaterialTypeEdit />,
    requiresAuth: true,
    requiresScopes: ['admin:material_type:manage'],
    meta: {
      title: 'Catálogo de materiais',
      breadcrumb: [
        { label: 'Resumo', href: '/material' },
        { label: 'Editar material', href: '/material/:uuid' },
      ],
      backButton: true,

      // getButton: (navigate) => ({
      //   label: 'Voltar',
      //   iconName: 'arrow_back_ios',
      //   onClick: () => navigate(-1),
      // }),
    },
  },
  {
    path: '/whatsapp-notifications',
    element: <WhatsAppNotifications />,
    requiresAuth: true,
    requiresScopes: ['admin:notifications:manage'],
    meta: {
      title: 'Notificações WhatsApp',
      breadcrumb: [
        {
          label: 'Notificações WhatsApp',
          href: '/whatsapp-notifications',
        },
      ],
    },
  },
  {
    path: '/batch-action',
    element: <BatchActionList />,
    requiresAuth: true,
    requiresMaster: true,
    meta: {
      title: 'Ações em lote',
      breadcrumb: [
        {
          label: 'Resumo',
          href: '/batch-action',
        },
      ],
    },
  },
  {
    path: '/batch-action/:uuid',
    element: <BatchActionDetail />,
    requiresAuth: true,
    requiresMaster: true,
    meta: {
      title: 'Ação em lote',
      breadcrumb: [
        {
          label: 'Ações em lote',
          href: '/batch-action',
        },
        {
          label: 'Detalhe',
          href: '/batch-action/:uuid',
        },
      ],
      backButton: true,
    },
  },
  {
    path: '/pos',
    element: <MaterialList />,
    requiresAuth: true,
    requiresScopes: ['material:view'],
    meta: {
      title: 'Materiais de PDV',
      breadcrumb: [
        {
          label: 'Inicio',
          href: '/pos',
        },
      ],
      getButton: [
        (_navigate) => ({
          label: 'Ação em lote',
          iconName: 'stacks',
          requiredScopes: ['batch_action:execute'],
          allowMaster: true,
          modal: ({ open, setOpen }) => <BatchActionModal open={open} setOpen={setOpen} />,
          onClick: () => {},
        }),
        (navigate) => ({
          label: 'Adicionar dispositivo',
          iconName: 'add_box',
          requiredScopes: ['material:create'],
          style: 'outline',
          onClick: () => navigate('/pos/create'),
        }),
      ],
    },
  },
  {
    path: '/pos/:uuid',
    element: <MaterialEdit />,
    requiresAuth: true,
    requiresScopes: ['material:view'],
    meta: {
      title: 'Materiais de PDV',
      breadcrumb: [
        {
          label: 'Inicio',
          href: '/pos',
        },
        {
          label: 'Editar Material de PDV',
          href: '/pos/:uuid',
        },
      ],
      backButton: true,

      // getButton: (navigate) => ({
      //   label: 'Voltar',
      //   iconName: 'arrow_back_ios',
      //   onClick: () => navigate(-1),
      // }),
    },
  },
  {
    path: '/campaign',
    element: <CampaignList />,
    requiresAuth: true,
    requiresScopes: ['campaign:view'],
    meta: {
      title: 'Campanhas',
      breadcrumb: [
        {
          label: 'Gerenciar',
          href: '/campaign',
        },
      ],
      getButton: [
        (navigate) => ({
          label: 'Criar campanha',
          iconName: 'hub',
          iconFill: 1,
          requiredScopes: ['campaign:create'],
          onClick: () => navigate('/campaign/create'),
        }),
      ],
    },
  },
  {
    path: '/campaign/:uuid',
    element: <CampaignDetails />,
    requiresAuth: true,
    requiresScopes: ['campaign:view'],
    meta: {
      title: 'Campanhas',
      breadcrumb: [
        {
          label: 'Gerenciar',
          href: '/campaign',
        },
        {
          label: 'Dados de campanha',
          href: '/campaign/:uuid',
        },
      ],
      backButton: true,

      // getButton: (navigate) => ({
      //   label: 'Voltar',
      //   iconName: 'arrow_back_ios',
      //   iconFill: 1,
      //   onClick: () => navigate(-1),
      // }),
    },
  },
  {
    path: '/campaign/edit/:uuid',
    element: <CampaignEdit />,
    requiresAuth: true,
    requiresScopes: ['campaign:edit'],
    meta: {
      title: 'Campanhas',
      breadcrumb: [
        {
          label: 'Gerenciar',
          href: '/campaign',
        },
        {
          label: 'Editar Campanha',
          href: '/campaign/edit/:uuid',
        },
      ],
      backButton: true,

      // getButton: (navigate) => ({
      //   label: 'Voltar',
      //   iconName: 'arrow_back_ios',
      //   onClick: () => navigate('/campaign'),
      // }),
    },
  },
  {
    path: '/campaign/create',
    element: <CampaignCreate />,
    requiresAuth: true,
    requiresScopes: ['campaign:create'],
    meta: {
      title: 'Campanhas',
      breadcrumb: [
        {
          label: 'Gerenciar',
          href: '/campaign',
        },
        {
          label: 'Criar campanha',
          href: '/campaign/create',
        },
      ],
      backButton: true,
      // getButton: (navigate) => ({
      //   label: 'Voltar',
      //   iconName: 'arrow_back_ios',
      //   iconFill: 1,
      //   onClick: () => navigate(-1),
      // }),
    },
  },
  {
    path: '/media',
    element: <MediaBrandList />,
    requiresAuth: true,
    requiresScopes: ['media:view'],
    meta: {
      title: 'Gestão de Mídias',
      breadcrumb: [
        {
          label: 'Resumo',
          href: '/media',
        },
      ],
      backButton: false,

      getButton: [
        (navigate) => ({
          label: 'Cadastrar nova mídia',
          iconName: 'upload_file',
          iconFill: 1,
          requiredScopes: ['media:create'],
          onClick: () => navigate('/media/create'),
        }),
      ],
    },
  },
  {
    path: '/role/create',
    element: <RoleCreate />,
    requiresAuth: true,
    requiresScopes: ['admin:roles:create'],
    meta: {
      title: 'Equipe e acessos',
      breadcrumb: [
        {
          label: 'Resumo',
          href: '/user',
        },
        {
          label: 'Novo perfil administrativo',
          href: '/role/create',
        },
      ],
      backButton: true,

      // getButton: (navigate) => ({
      //   label: 'Voltar',
      //   iconName: 'arrow_back_ios',
      //   onClick: () => navigate(-1),
      // }),
    },
  },
  {
    path: '/media/create',
    element: <MediaCreate />,
    requiresAuth: true,
    requiresScopes: ['media:create'],
    meta: {
      title: 'Gestão de Mídias',
      breadcrumb: [
        {
          label: 'Resumo',
          href: '/media',
        },
        {
          label: 'Upload da sua mídia para a biblioteca',
          href: '/media/create',
        },
      ],
      backButton: true,

      // getButton: (navigate) => ({
      //   label: 'Voltar',
      //   iconName: 'arrow_back_ios',
      //   onClick: () => navigate(-1),
      // }),
    },
  },
  {
    path: '/media/:brandUuid',
    element: <MediaList />,
    requiresAuth: true,
    requiresScopes: ['media:view'],
    meta: {
      title: 'Gestão de Mídias',
      breadcrumb: (params, state) => [
        { label: 'Resumo', href: '/media' },
        {
          label: state?.brandName || 'Marca',
          href: `/media/${params.brandUuid}`,
        },
      ],
      backButton: true,
      getButton: [
        (navigate) => ({
          label: 'Cadastrar nova mídia',
          iconName: 'upload_file',
          iconFill: 1,
          requiredScopes: ['media:create'],
          onClick: () => navigate('/media/create'),
        }),
      ],
    },
  },
  {
    path: '/media/:brandUuid/:mediaUuid',
    element: <MediaEdit />,
    requiresAuth: true,
    requiresScopes: ['media:view'],
    meta: {
      title: 'Gestão de Mídias',
      breadcrumb: (params, state) => [
        { label: 'Resumo', href: '/media' },
        {
          label: state?.brandName || 'Marca',
          href: `/media/${params.brandUuid}`,
        },
        {
          label: 'Detalhes da mídia',
          href: `/media/${params.brandUuid}/${params.mediaUuid}`,
        },
      ],
      backButton: true,

      // getButton: (navigate) => ({
      //   label: 'Voltar',
      //   iconName: 'arrow_back_ios',
      //   iconFill: 1,
      //   onClick: () => navigate(-1),
      // }),
    },
  },
  {
    path: '/role/:uuid',
    element: <RoleEdit />,
    requiresAuth: true,
    requiresScopes: ['admin:roles:edit'],
    meta: {
      title: 'Equipe e acessos',
      breadcrumb: [
        {
          label: 'Resumo',
          href: '/user',
        },
        {
          label: 'Editar perfil administrativo',
          href: '/role/:uuid',
        },
      ],
      backButton: true,

      // getButton: (navigate) => ({
      //   label: 'Voltar',
      //   iconName: 'arrow_back_ios',
      //   onClick: () => navigate(-1),
      // }),
    },
  },
  {
    path: '/facility',
    element: <FacilityList />,
    requiresAuth: true,
    requiresScopes: ['admin:facilities:view'],
    meta: {
      title: 'Estabelecimentos',
      breadcrumb: [
        {
          label: 'Início',
          href: '/facility',
        },
      ],
      getButton: [
        (navigate) => ({
          label: 'Cadastrar novo estabelecimento',
          iconName: 'storefront',
          requiredScopes: ['admin:facilities:create'],
          onClick: () => navigate('/media/create'),
        }),
      ],
    },
  },
  {
    path: '/login',
    element: <Login />,
    requiresAuth: false,
    meta: {
      title: 'Login',
      breadcrumb: [
        {
          label: 'Início',
          href: '/login',
        },
      ],
    },
  },
  {
    path: '/verification-receive',
    element: <LoginVerificationCodeReceive />,
    requiresAuth: false,
    meta: {
      title: 'Primeiro acesso',
      breadcrumb: [
        {
          label: 'Início',
          href: '/verification-receive',
        },
      ],
    },
  },
  {
    path: '/update-contact-info',
    element: <LoginUpdateContactInfo />,
    requiresAuth: false,
    meta: {
      title: 'Configurar informações de contato',
      breadcrumb: [
        {
          label: 'Início',
          href: '/update-contact-info',
        },
      ],
    },
  },
  {
    path: '/verification-validate',
    element: <LoginVerificationCodeValidate />,
    requiresAuth: false,
    meta: {
      title: 'Primeiro acesso',
      breadcrumb: [
        {
          label: 'Início',
          href: '/verification-validate',
        },
      ],
    },
  },
  {
    path: '/define-password',
    element: <LoginDefinePassword />,
    requiresAuth: false,
    meta: {
      title: 'Definição de senha',
      breadcrumb: [
        {
          label: 'Início',
          href: '/define-password',
        },
      ],
    },
  },
];
